import { useEffect } from "react";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";
import { useLocalStorage } from "usehooks-ts";

import type { Flow } from "src/api/flowTypes";
import { Button } from "src/design-system/Button";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { useLiveAnalyticsAccess } from "src/flow/analytics/useLiveAnalyticsAccess";
import { useFlowNavSidebarCollapsed } from "src/flow/useFlowNavSidebarCollapsed";
import { useCapabilities } from "src/hooks/useCapabilities";
import { flowFolderScope } from "src/hooks/useCapabilities/flowFolderScope";
import { type FlowPageParams, getFlowSubpageUrl } from "src/router/urls";
import { useCurrentPage } from "src/router/useCurrentPage";
import { useIsTaktileStaffEmail } from "src/store/AuthStore";
import { useParamsDecode } from "src/utils/useParamsDecode";

type IntroModalProps = {
  onClose: () => void;
  onConfirm: () => void;
};

const EXAMPLE_QUESTION = "What is my decision volume in the last 7 days?";

type VolumeBar = {
  day: string;
  height: string;
  color: string;
};

// A still life of the chart Insights returns for EXAMPLE_QUESTION:
// seven days of volume rising into the weekend.
const VOLUME_BARS: VolumeBar[] = [
  { day: "mon", height: "h-5.5", color: "bg-indigo-200" },
  { day: "tue", height: "h-7", color: "bg-indigo-200" },
  { day: "wed", height: "h-6.5", color: "bg-indigo-200" },
  { day: "thu", height: "h-9", color: "bg-indigo-300" },
  { day: "fri", height: "h-8", color: "bg-indigo-300" },
  { day: "sat", height: "h-10", color: "bg-indigo-400" },
  { day: "sun", height: "h-12", color: "bg-indigo-600" },
];

const IntroModal: React.FC<IntroModalProps> = ({ onClose, onConfirm }) => (
  <Modal open onClose={onClose}>
    <Modal.Header description="Monitor and investigate flow performance by asking a question.">
      Insights is now available for your flows!
    </Modal.Header>
    <Modal.Content>
      {/* Pulls the preview up under the header: Modal.Header's pb-3 plus
          Modal.Content's p-5 otherwise leave a 32px gap. */}
      <div className="-mt-2 flex flex-col gap-y-2.5">
        <div className="flex items-center gap-x-1.5 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2">
          <Icon icon={faSparkles} size="xs" variant="active" />
          <span className="font-inter-normal-13px text-gray-800">
            {EXAMPLE_QUESTION}
          </span>
          <span className="h-4 w-px bg-indigo-600" />
        </div>
        <div className="rounded-lg border border-gray-200 px-3.5 py-3">
          <div className="mb-2.5 flex items-center gap-x-2">
            <span className="font-inter-semibold-12px text-gray-800">
              Decision volume, last 7 days
            </span>
            <span className="ml-auto font-inter-semibold-12px text-green-600">
              +12.4%
            </span>
          </div>
          <div className="flex h-12 items-end gap-x-1.5">
            {VOLUME_BARS.map(({ day, height, color }) => (
              <span
                key={day}
                className={twJoin("flex-1 rounded-t", height, color)}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal.Content>
    {/* Always-on divider per the modal spec: Modal.Content only draws its
        own bottom border while the content scrolls. */}
    <div className="border-gray-200 border-t" />
    <Modal.Footer
      secondaryButton={false}
      primaryButton={
        <Button
          dataLoc="flow-insights-intro-confirm"
          variant="primary"
          onClick={onConfirm}
        >
          Explore Insights
        </Button>
      }
    />
  </Modal>
);

export const FlowInsightsIntroModal: React.FC<{ flow: Flow | undefined }> = ({
  flow,
}) => {
  const { orgId, wsId, flow_id: flowId } = useParamsDecode<FlowPageParams>();
  const navigate = useNavigate();
  const { canAccess } = useLiveAnalyticsAccess();
  const { flows } = useCapabilities();
  const { isFlowInsightsPage } = useCurrentPage();
  const isTaktileStaff = useIsTaktileStaffEmail();
  const [introSeen, setIntroSeen] = useLocalStorage(
    `flowInsightsIntroSeen:${wsId}`,
    false,
  );

  const canOpenInsights =
    canAccess && flow !== undefined && flows.canEdit(flowFolderScope(flow));
  const needsIntro = canOpenInsights && !isTaktileStaff;
  const showIntro = needsIntro && !introSeen && !isFlowInsightsPage;
  const [, setSidebarCollapsed] = useFlowNavSidebarCollapsed();

  useEffect(() => {
    if (needsIntro && isFlowInsightsPage) {
      setIntroSeen(true);
    }
  }, [needsIntro, isFlowInsightsPage, setIntroSeen]);

  useEffect(() => {
    if (showIntro) {
      setSidebarCollapsed(false);
    }
  }, [showIntro, setSidebarCollapsed]);

  if (!showIntro) {
    return null;
  }

  const handleClose = () => setIntroSeen(true);
  const handleConfirm = () => {
    setIntroSeen(true);
    navigate(getFlowSubpageUrl(orgId, wsId, flowId, "insights"));
  };

  return <IntroModal onClose={handleClose} onConfirm={handleConfirm} />;
};
